import React, { useState, memo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { Heart, Info, Search, Globe, Award, Star, Sparkles, X } from 'lucide-react';

// Advanced animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.8) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
`;

const ItemCard = styled.div`
  background: transparent;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  -webkit-tap-highlight-color: transparent;

  /* Desktop hover effects only */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 
        0 20px 50px rgba(0, 0, 0, 0.2),
        0 8px 20px rgba(0, 0, 0, 0.12);
    }
  }

  /* Touch feedback without flickering */
  &:active {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.15),
      0 4px 10px rgba(0, 0, 0, 0.1);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 400px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.4) 60%,
      transparent 100%
    );
    z-index: 2;
  }
`;

const ItemImage = styled.img<{ $loaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
  opacity: ${props => props.$loaded ? 1 : 0};
  filter: brightness(1.05) contrast(1.1) saturate(1.15);

  ${ItemCard}:hover & {
    transform: scale(1.05);
    filter: brightness(1.1) contrast(1.15) saturate(1.25);
  }
`;

const ImagePlaceholder = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.4s ease;
  font-size: 4rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const PrimaryBadge = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(255, 215, 0, 0.95);
  color: #8B0000;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  z-index: 3;
  transition: all 0.2s ease;
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OverlayTitle = styled.h3`
  font-size: 24px;
  font-weight: 800;
  color: white;
  margin: 0;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.7);
  line-height: 1.2;
`;

const OverlayPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 900;
  background: linear-gradient(135deg, #FFD700, #FFEB3B);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin-top: 8px;
  letter-spacing: -1px;
  display: none; /* Hide price */
`;

const ViewDetailsOverlay = styled.button`
  background: rgba(255, 215, 0, 0.95);
  color: #8B0000;
  border: none;
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  align-self: flex-start;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255, 248, 220, 0.98);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
`;

const HeartIcon = styled(Heart)<{ $liked: boolean }>`
  position: absolute;
  top: 16px;
  left: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  fill: ${props => props.$liked ? '#ff4757' : 'transparent'};
  color: ${props => props.$liked ? '#ff4757' : 'rgba(255, 255, 255, 0.8)'};
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  z-index: 3;
  width: 24px;
  height: 24px;

  &:hover {
    transform: scale(1.1);
    color: ${props => props.$liked ? '#ff4757' : 'white'};
  }
`;

// Removed SparkleEffect for cleaner design

const Content = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  position: relative;
  border-radius: 0 0 20px 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  flex: 1;
  line-height: 1.2;
  letter-spacing: -0.5px;
`;

const Price = styled.span`
  font-size: 20px;
  font-weight: 900;
  background: linear-gradient(135deg, #8b4513 0%, #a0522d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-left: 12px;
  position: relative;
  display: none; /* Hide price */

  &::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(135deg, #8b4513 0%, #a0522d 100%);
    border-radius: 1px;
    opacity: 0.3;
  }
`;

const Description = styled.p`
  color: #555;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 400;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const Tag = styled.span<{ variant: string }>`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.variant) {
      case 'CLASSIC':
        return `
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          color: #1976d2;
          border: 1px solid rgba(25, 118, 210, 0.2);
        `;
      case 'SPICY':
        return `
          background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
          color: #d32f2f;
          border: 1px solid rgba(211, 47, 47, 0.2);
        `;
      case 'FRESH':
        return `
          background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
          color: #388e3c;
          border: 1px solid rgba(56, 142, 60, 0.2);
        `;
      default:
        return `
          background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
          color: #666;
          border: 1px solid rgba(102, 102, 102, 0.2);
        `;
    }
  }}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const IngredientsButton = styled.button`
  background: linear-gradient(135deg, #FFD700 0%, #FFC300 50%, #DAA520 100%);
  color: #8B0000;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: 
    0 4px 12px rgba(255, 215, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  -webkit-tap-highlight-color: transparent;

  /* Desktop hover effects only */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 
        0 6px 16px rgba(255, 215, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.4);
      background: linear-gradient(135deg, #FFF8DC 0%, #FFD700 50%, #B8860B 100%);
    }
  }

  /* Touch feedback without aggressive transforms */
  &:active {
    transform: translateY(0px);
    box-shadow: 
      0 2px 8px rgba(255, 215, 0, 0.4),
      inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

// Modal Overlay
const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

// Modal Content
const ModalContent = styled.div<{ $isOpen: boolean }>`
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 
    0 24px 48px rgba(0, 0, 0, 0.2),
    0 8px 16px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transform: ${props => props.$isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #8B0000 0%, #FFD700 100%);
  border-radius: 24px 24px 0 0;
  color: white;
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 8px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const ModalSubtitle = styled.p`
  font-size: 1rem;
  margin: 0;
  opacity: 0.9;
  font-style: italic;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: #8B0000;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IngredientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const IngredientChip = styled.div`
  background: linear-gradient(135deg, #FFF8DC 0%, #F4D03F 100%);
  color: #8B0000;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(218, 165, 32, 0.3);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  }
`;

const AllergensList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AllergenChip = styled.div`
  background: linear-gradient(135deg, #FFE4E1 0%, #FFC0CB 100%);
  color: #8B0000;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(255, 182, 193, 0.5);
`;

const PriceDisplay = styled.div`
  background: linear-gradient(135deg, #8B0000 0%, #FFD700 100%);
  color: white;
  padding: 16px;
  border-radius: 16px;
  text-align: center;
  margin-top: 16px;
  display: none; /* Hide price */
`;

const PriceText = styled.div`
  font-size: 2rem;
  font-weight: 800;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const RatingStars = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  display: flex;
  gap: 2px;
  z-index: 3;
`;

const StarIcon = styled(Star)<{ $filled: boolean; delay: number }>`
  width: 12px;
  height: 12px;
  fill: ${props => props.$filled ? '#ffd700' : 'transparent'};
  color: #ffd700;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  animation: ${sparkle} 2s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
`;

interface MenuItemProps {
  item: {
    id: string;
    name: string;
    description: string;
    price: number;
    image: string;
    category: string;
    tags?: string[];
    allergens?: string[];
    ingredients?: string[];
    addOns?: Array<{
      id: string;
      name: string;
      price: number;
    }>;
  };
}

const MenuItem: React.FC<MenuItemProps> = memo(({ item }) => {
  const [liked, setLiked] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Memoize handlers to prevent unnecessary re-renders
  const handleLikeToggle = useCallback(() => {
    setLiked(prev => !prev);
  }, []);

  const handleIngredientsClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // Get emoji for placeholder based on category
  const getPlaceholderEmoji = () => {
    switch (item.category) {
      case 'signature-burgers': return '🍔';
      case 'royale-bites': return '🍟';
      case 'chicken-sandwiches': return '🐔';
      case 'salads': return '🥗';
      default: return '🍽️';
    }
  };

  // Get primary tag for display
  const primaryTag = item.tags?.[0];

  return (
    <ItemCard ref={cardRef}>
      <ImageContainer>
        {!imageError && (
          <ItemImage 
            src={item.image} 
            alt={`${item.name} - Royale with Cheese Detroit`}
            loading="lazy"
            $loaded={imageLoaded}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
        <ImagePlaceholder $visible={!imageLoaded || imageError}>
          {getPlaceholderEmoji()}
        </ImagePlaceholder>
        
        {primaryTag && (
          <PrimaryBadge>
            {primaryTag}
          </PrimaryBadge>
        )}

        <ImageOverlay>
          <OverlayTitle>{item.name}</OverlayTitle>
          {/* <OverlayPrice>${item.price.toFixed(2)}</OverlayPrice> */}
          <ViewDetailsOverlay onClick={handleIngredientsClick}>
            <Info size={16} />
            View Details
          </ViewDetailsOverlay>
        </ImageOverlay>
      </ImageContainer>

      {/* Ingredients Modal - Rendered using Portal to break out of container */}
      {modalOpen && createPortal(
        <ModalOverlay $isOpen={modalOpen} onClick={handleCloseModal}>
          <ModalContent $isOpen={modalOpen} onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{item.name}</ModalTitle>
              <ModalSubtitle>Discover what makes this dish special</ModalSubtitle>
              <CloseButton onClick={handleCloseModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Ingredients Section */}
              {item.ingredients && item.ingredients.length > 0 && (
                <Section>
                  <SectionTitle>
                    🧑‍🍳 Premium Ingredients
                  </SectionTitle>
                  <IngredientsGrid>
                    {item.ingredients.map((ingredient, index) => (
                      <IngredientChip key={index}>
                        {ingredient}
                      </IngredientChip>
                    ))}
                  </IngredientsGrid>
                </Section>
              )}

              {/* Allergens Section */}
              {item.allergens && item.allergens.length > 0 && (
                <Section>
                  <SectionTitle>
                    ⚠️ Allergen Information
                  </SectionTitle>
                  <AllergensList>
                    {item.allergens.map((allergen, index) => (
                      <AllergenChip key={index}>
                        {allergen}
                      </AllergenChip>
                    ))}
                  </AllergensList>
                </Section>
              )}

              {/* Add-ons Section */}
              {item.addOns && item.addOns.length > 0 && (
                <Section>
                  <SectionTitle>
                    ➕ Available Add-ons
                  </SectionTitle>
                  {item.addOns.map((addOn) => (
                    <IngredientChip key={addOn.id}>
                      {addOn.name} {/* (+${addOn.price.toFixed(2)}) */}
                    </IngredientChip>
                  ))}
                </Section>
              )}

              {/* Description Section */}
              <Section>
                <SectionTitle>
                  📝 Chef's Description
                </SectionTitle>
                <p style={{ 
                  color: '#2d3748', 
                  lineHeight: '1.6', 
                  fontSize: '1rem',
                  background: 'rgba(255, 248, 220, 0.5)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(218, 165, 32, 0.2)',
                  margin: 0
                }}>
                  {item.description}
                </p>
              </Section>

              {/* Price Section - Hidden */}
              {/* <PriceDisplay>
                <PriceText>${item.price.toFixed(2)}</PriceText>
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginTop: '4px' }}>
                  Premium Quality • Detroit Made
                </div>
              </PriceDisplay> */}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </ItemCard>
  );
});

MenuItem.displayName = 'MenuItem';

export default MenuItem;